import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  fetchLibkeyArticles,
  LibkeyArticleResult,
  LibkeyReferenceId,
} from 'query/study/LibKey/Articles';
import { useCurrentReviewer } from 'hooks/reviewers/useCurrentReviewer';
import { QueryProvider } from 'query/QueryProvider';

/**
 * `undefined` — not fetched yet (or fetch in flight).
 * `null`      — fetched, but LibKey had no result for this reference.
 * object      — the fetched LibKey result.
 */
export type LibkeyResult = LibkeyArticleResult | null | undefined;

type LibkeyContextValue = {
  // Native LibKey integration is active: the reviewer has selected a library,
  // or the Open Alex default applies to them.
  isEnabled: boolean;
  // Offer the LibKey Nomad → built-in migration nudge: the reviewer has not
  // selected their own library. NOT mutually exclusive with isEnabled — a
  // reviewer on the Open Alex default has both true, and must keep the
  // `ref-ids` hook rendered so the nudge can harvest their Nomad library.
  // Only selecting a library ends eligibility.
  nomadMigrationEligible: boolean;
  // False while the current reviewer is still loading, so consumers can tell
  // "LibKey is off" apart from "we don't know yet".
  isResolved: boolean;
  // The current reviewer's id, used to scope the migration nudge's per-device
  // dismissal flags so two reviewers sharing a browser don't inherit each
  // other's state. Null until the reviewer resolves.
  reviewerId: number | null;
  // How many reference rows are currently rendered. The Nomad migration nudge
  // waits for this to become non-zero before harvesting (the extension only
  // injects its buttons onto reference rows) and re-harvests when it grows.
  visibleReferenceCount: number;
  // The reviewer's effective library is the Open Access (Open Alex) library —
  // Reviewer-level, so it applies to every reference at once.
  openAccessLibrary: boolean;
  getResult: (referenceId: LibkeyReferenceId) => LibkeyResult;
};

const DEFAULT_CONTEXT: LibkeyContextValue = {
  isEnabled: false,
  nomadMigrationEligible: false,
  isResolved: true,
  reviewerId: null,
  visibleReferenceCount: 0,
  openAccessLibrary: false,
  getResult: () => undefined,
};

export const LibkeyContext = createContext<LibkeyContextValue>(DEFAULT_CONTEXT);

type LibkeyProviderProps = {
  reviewId: string;
  referenceIds: LibkeyReferenceId[];
  children?: React.ReactNode;
};

// Self-contained: provides its own QueryProvider so callers don't have to wire
// up react-query just to render the study lists. The data-fetching inner
// component must sit *below* the QueryProvider, hence the split.
export const LibkeyProvider = (props: LibkeyProviderProps) => (
  <QueryProvider>
    <LibkeyProviderInner {...props} />
  </QueryProvider>
);

const LibkeyProviderInner = ({
  reviewId,
  referenceIds,
  children,
}: LibkeyProviderProps) => {
  const { currentReviewer, isLoading } = useCurrentReviewer();
  const isEnabled = currentReviewer?.libkey_enabled ?? false;
  const nomadMigrationEligible =
    currentReviewer?.libkey_nomad_migration_eligible ?? false;
  const openAccessLibrary =
    currentReviewer?.libkey_open_access_library ?? false;
  const isResolved = !isLoading;
  const reviewerId = currentReviewer?.id ?? null;

  // Keyed by the id coerced to a string, so a numeric id and its string form
  // (the endpoint always echoes back what it received) resolve to one entry.
  const [results, setResults] = useState<
    Map<string, LibkeyArticleResult | null>
  >(() => new Map());

  // Reference ids already fetched OR currently in flight. A ref (not state) so
  // re-batching reads the latest set synchronously and never re-fetches an id
  // that another batch is already handling.
  const requestedIds = useRef<Set<LibkeyReferenceId>>(new Set());

  // Guards against applying a resolved batch after the provider has unmounted.
  // Crucially NOT tied to the visible set: a batch in flight when the list
  // paginates/sorts/filters must still apply its results.
  const mounted = useRef(true);
  useEffect(() => {
    // Re-assert on setup so the ref survives React 18 StrictMode's
    // setup → cleanup → setup remount (cleanup alone would leave it false).
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const visibleIds = useMemo(
    () => Array.from(new Set(referenceIds.filter(Boolean))),
    [referenceIds]
  );
  const visibleKey = visibleIds.join(',');

  // A change of review invalidates everything keyed by the previous review's
  // ids, so clear the requested marks and cached results before re-fetching.
  useEffect(() => {
    requestedIds.current = new Set();
    setResults(new Map());
  }, [reviewId]);

  useEffect(() => {
    if (!isEnabled || !reviewId) {
      return;
    }

    const idsToFetch = visibleIds.filter((id) => !requestedIds.current.has(id));
    if (idsToFetch.length === 0) {
      return;
    }

    // Mark as in flight *before* the request so overlapping re-batches dedupe.
    idsToFetch.forEach((id) => requestedIds.current.add(id));

    fetchLibkeyArticles(reviewId, idsToFetch)
      .then((articles) => {
        if (!mounted.current) {
          return;
        }

        // An empty resolve is the *handled* failure path: fetchLibkeyArticles
        // caught the per-batch errors (transient ones having already exhausted
        // their in-batch p-retry attempts) and resolved empty rather than
        // rejecting. Drop the marks so these ids get another attempt on the next
        // visible-set change — a transient blip should self-heal. Clearing a ref
        // does not trigger a render, so this can't loop. Contrast the .catch
        // below: that is the *unhandled* crash path and deliberately does NOT
        // retry.
        if (articles.length === 0) {
          idsToFetch.forEach((id) => requestedIds.current.delete(id));
          return;
        }

        // Merging into the id-keyed map is idempotent and always valid, so we
        // apply results even when the visible set has changed mid-flight.
        setResults((prev) => {
          const next = new Map(prev);
          articles.forEach(({ reference_id, result }) => {
            next.set(String(reference_id), result);
          });
          return next;
        });
      })
      .catch(() => {
        // The *unhandled* failure path. fetchLibkeyArticles already retries
        // transient errors internally (p-retry) and swallows per-batch failures
        // into an empty resolve (handled above), so the only thing that reaches
        // here is an unexpected, unhandled error — in practice a server crash
        // (an unhandled 500, a malformed response, the request layer throwing).
        //
        // Deliberately do NOT clear the marks. Leaving these ids marked means we
        // will NOT re-request them when the visible set next changes (paginate /
        // sort / filter). This is intentional, not a stranded-reference bug:
        // re-hitting a server that is already falling over only compounds the
        // problem, so we'd rather these references render nothing than hammer a
        // broken endpoint. A full page reload resets the provider and gives it a
        // fresh attempt, by which point the server may have recovered. (Genuinely
        // transient errors are not affected — those are retried by p-retry inside
        // fetchLibkeyArticles before they could ever reach this catch.)
        //
        // See the "does not retry ids after an unhandled batch rejection" spec,
        // which guards this no-retry behaviour.
      });
    // visibleKey is the stable projection of visibleIds; re-batch when it,
    // the review, or the enabled state changes. No cleanup cancellation: an
    // in-flight batch must still apply its results when the visible set churns.
  }, [isEnabled, reviewId, visibleKey]);

  const value = useMemo<LibkeyContextValue>(
    () => ({
      isEnabled,
      nomadMigrationEligible,
      isResolved,
      reviewerId,
      visibleReferenceCount: visibleIds.length,
      openAccessLibrary,
      getResult: (referenceId: LibkeyReferenceId) =>
        results.get(String(referenceId)),
    }),
    [
      isEnabled,
      isResolved,
      nomadMigrationEligible,
      reviewerId,
      visibleIds.length,
      openAccessLibrary,
      results,
    ]
  );

  return (
    <LibkeyContext.Provider value={value}>{children}</LibkeyContext.Provider>
  );
};

/**
 * Read the LibKey state for a single reference. Safe to call outside a
 * provider — it falls back to the disabled default (renders nothing).
 */
export const useLibkeyResult = (referenceId: LibkeyReferenceId) => {
  const { isEnabled, openAccessLibrary, getResult } = useContext(LibkeyContext);

  return useMemo(
    () => ({
      isEnabled,
      openAccessLibrary,
      result: isEnabled ? getResult(referenceId) : undefined,
    }),
    [isEnabled, openAccessLibrary, getResult, referenceId]
  );
};

/**
 * Whether to offer the LibKey Nomad → built-in migration nudge. Safe to call
 * outside a provider — falls back to the disabled default.
 */
export const useLibkeyNomadMigrationEligible = (): boolean =>
  useContext(LibkeyContext).nomadMigrationEligible;

/**
 * The current reviewer's id, for scoping the migration nudge's per-device
 * dismissal flags. Null until the reviewer resolves (or outside a provider).
 */
export const useLibkeyMigrationReviewerId = (): number | null =>
  useContext(LibkeyContext).reviewerId;

/**
 * How many reference rows are currently rendered. The migration nudge gates its
 * Nomad-button harvest on this (see LibkeyNomadMigration).
 */
export const useLibkeyVisibleReferenceCount = (): number =>
  useContext(LibkeyContext).visibleReferenceCount;
